import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../../common/CCUtils"
import { log } from "../../../../common/Logging"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { loadGenericAssets } from "../../../generic-sprites/GenericAssets"
import { ColorUtils } from "../../../utils/ColorUtils"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { MakeVaperPartsStore } from "../MakeVaperPartsStore"
import { loadSharedAssets } from "../MakeYourOwnVaperAssets"
import { MakeVaperContentKind, MakeVaperScreenContent } from "../types/MakeVaperScreenContentTypes"
import { MakeVaperScreenType, MakeVaperSpudPosition, MakeVaperSpudSize, MakeVaperTitleTextPosition } from "../types/MakeVaperScreenValueTypes"
import { MakeVaperActivityScreens, MakeVaperChoices, MakeVaperPart } from "../types/MakeYourOwnVaperTypes"
import { MakeVaperScreenBuilder } from "../utils/MakeVaperScreenBuilder"
import { loadAssets, MakeVaperPt2Audio, makeVaperReviewPartAudio } from "./MakeYourOwnVaperPartTwoAssets"

export class MakeYourOwnVaperPartTwoState extends Phaser.State {
    private defaultSpud: MakeVaperChoices = {
        cool: true,
        athletic: true,
        exciting: true,
        smart: true,
        popular: true
    }

    private screen: MakeVaperScreenBuilder
    private spriteGroup: Phaser.Group
    private ecigGroup: Phaser.Group

    async preload() {
        loadGenericAssets(this.game)
        loadSharedAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        MakeVaperPartsStore.newInstance()
        await PhaserUtils.sharedCreateFunctions(this.game)
        MakeVaperPartsStore.instance.spud = MakeVaperPartsStore.getCurrentStudentSpud()
        if (MakeVaperPartsStore.instance.spud == null) {
            log.warn("Looks like you don't have a spud yet from part one! Using default.")
            MakeVaperPartsStore.instance.spud = this.defaultSpud
        }

        ColorUtils.setBackgroundColorLightGray(this.game)
        if (!PhaserUtils.loggedIn() && !CCUtils.debug) {
            PhaserUtils.notLoggedInMessage(this.game)
            return
        }

        this.screen = MakeVaperScreenBuilder.create(this.game, ActivityKey.MAKE_YOUR_OWN_VAPER_PT_2, this.allScreens)
        this.spriteGroup = this.game.add.group()
        this.ecigGroup = this.game.add.group()
        this.screen.showEcig(true)
    }

    get allScreens(): Array<MakeVaperActivityScreens> {
        return [
            MakeYourOwnVaperPartTwoState.introScreen(),
            this.reviewSpud(),
            MakeYourOwnVaperPartTwoState.compareIntroScreen(),
            this.compareScreen(),
            MakeYourOwnVaperPartTwoState.agreementIntroScreen(),
            this.agreementScreen(),
            MakeYourOwnVaperPartTwoState.exitScreen()
        ]
    }

    private static introScreen = (): MakeVaperActivityScreens => {
        return {
            type: MakeVaperScreenType.TIMED,
            titleImage: true,
            spudTitle: true,
            screenAudio: MakeVaperPt2Audio.INTRO,
            screenContent: [
                {
                    kind: MakeVaperContentKind.LETTERED_TEXT,
                    delay: 3000,
                    text: "Review your Mr. Spud."
                },
                {
                    kind: MakeVaperContentKind.LETTERED_TEXT,
                    delay: 6000,
                    text: "See what your class thinks about vapers and how much the Mr. Spuds match."
                },
                {
                    kind: MakeVaperContentKind.LETTERED_TEXT,
                    delay: 12500,
                    text: "Try to reach an agreement on about what kids who vape are like."
                }
            ]
        }
    }

    private reviewSpud = (): MakeVaperActivityScreens => {
        return {
            type: MakeVaperScreenType.TIMED,
            centerContent: true,
            enableOnAudioStop: false,
            screenAudio: MakeVaperPt2Audio.YOUR_SPUD,
            spudPosition: MakeVaperSpudPosition.CENTER,
            screenContent: [
                this.reviewSelectionScreenContent(1, MakeVaperPart.SMART),
                this.reviewSelectionScreenContent(2, MakeVaperPart.COOL),
                this.reviewSelectionScreenContent(3, MakeVaperPart.POPULAR),
                this.reviewSelectionScreenContent(4, MakeVaperPart.EXCITING),
                this.reviewSelectionScreenContent(5, MakeVaperPart.ATHLETIC),
                {
                    kind: MakeVaperContentKind.FUNCTION,
                    delay: 12000,
                    function: () => {
                        const audio = this.game.add.audio(MakeVaperPt2Audio.CLICK_NEXT).play()
                        audio.onStop.add(() => this.screen.currentButton.enable())
                    }
                }
            ]
        }
    }

    private static compareIntroScreen = (): MakeVaperActivityScreens => {
        return {
            type: MakeVaperScreenType.TIMED,
            centerContent: true,
            screenAudio: MakeVaperPt2Audio.COMPARE_SPUD,
            hideSpud: true,
            screenContent: [
                {
                    kind: MakeVaperContentKind.CENTER_TEXT,
                    title: "Step 2",
                    text: "See how your Mr. Spud compares to the ones your classmates made.",
                }
            ]
        }
    }

    private compareScreen = (): MakeVaperActivityScreens => {
        return {
            type: MakeVaperScreenType.STEPS,
            centerContent: true,
            showFullSpud: MakeVaperPartsStore.instance.spud,
            spudSize: MakeVaperSpudSize.MEDIUM,
            compareColumns: true,
            compareSpudSize: MakeVaperSpudSize.MEDIUM,
            titleText: {
                text: "",
                position: MakeVaperTitleTextPosition.CENTER
            },
            screenContent: [
                this.compareSelectionScreenContent(MakeVaperPart.SMART),
                this.compareSelectionScreenContent(MakeVaperPart.COOL),
                this.compareSelectionScreenContent(MakeVaperPart.POPULAR),
                this.compareSelectionScreenContent(MakeVaperPart.EXCITING),
                this.compareSelectionScreenContent(MakeVaperPart.ATHLETIC)
            ]
        }
    }

    private static agreementIntroScreen = (): MakeVaperActivityScreens => {
        return {
            type: MakeVaperScreenType.TIMED,
            centerContent: true,
            hideSpud: true,
            screenAudio: MakeVaperPt2Audio.AGREEMENT,
            screenContent: [
                {
                    kind: MakeVaperContentKind.CENTER_TEXT,
                    title: "Step 3",
                    text: "Reach an agreement with your classmates."
                }
            ]

        }
    }

    private agreementScreen = (): MakeVaperActivityScreens => {
        return {
            type: MakeVaperScreenType.AGREEMENT,
            centerContent: true,
            showFullSpud: MakeVaperPartsStore.instance.spud,
            spudSize: MakeVaperSpudSize.SMALL,
            compareColumns: true,
            compareSpudSize: MakeVaperSpudSize.MEDIUM,
            screenContent: []
        }
    }

    private static exitScreen = (): MakeVaperActivityScreens => {
        return {
            type: MakeVaperScreenType.EXIT,
            centerContent: true,
            spudSize: MakeVaperSpudSize.MEDIUM,
            compareColumns: true,
            compareSpudSize: MakeVaperSpudSize.MEDIUM,
            screenContent: []
        }
    }

    private compareSelectionScreenContent = (part: MakeVaperPart): MakeVaperScreenContent => ({
        kind: MakeVaperContentKind.COMPARE,
        response: MakeVaperPartsStore.instance.spud[part],
        part
    })

    private reviewSelectionScreenContent = (index: number, part: MakeVaperPart, delay = 2000): MakeVaperScreenContent => ({
        kind: MakeVaperContentKind.FUNCTION,
        delay: delay * index,
        function: this.createReviewParts(part)
    })

    private createReviewParts = (part: MakeVaperPart) => () => {
        const answeredYes = MakeVaperPartsStore.instance.spud[part]
        const audioType = answeredYes ? "yes" : "no"
        const audio = this.game.add.audio(makeVaperReviewPartAudio(part, audioType))
        log.debug(`Create review parts part ${part} answered yes ${answeredYes}`)
        this.screen.spud.setPartResponse(part, answeredYes)
        audio.play()
    }
}
